import React, { useEffect, useMemo, useState } from "react";
import MyTable from "../ui/Table";
import {
  Dropdown,
  Button,
  Menu,
  Input,
  Select,
  Space,
  Tabs,
  Badge,
} from "antd";
import {
  CopyOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  FileSyncOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import GlowButton from "../components/GlowButton";
import { useNavigate } from "react-router-dom";
import AddBookModal from "../modals/AddBookModal";
import apiRoutes from "../api/apiRoutes";
import { apiDelete, apiGet, apiPost, apiPostFormData } from "../api/request";
import DeleteBookConfirmationModal from "../modals/DeleteBookConfirmationModal";

const Ebook = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    code: "",
    author: "",
    language_id: 0,
    category_id: 0,
    description: "",
    is_active: "false",
    mode: "Public",
    lastScriptNumber: 0,
    is_featured: false,
    is_protected: false,
  });
  const [ebooks, setEbooks] = useState([]);
  const [activeTab, setActiveTab] = useState("published");
  const [unpublishedEbooks, setUnpublishedEbooks] = useState([]);

  const [dropdownOptions, setDropdownOptions] = useState({
    languages: [],
    states: [],
    categories: [],
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [modalKey, setModalKey] = useState(0);
  const [coverFileList, setCoverFileList] = useState([]);
  const [bannerFileList, setBannerFileList] = useState([]);
  const [previewCover, setPreviewCover] = useState(null);
  const [previewBanner, setPreviewBanner] = useState(null);

  const IMAGE_BASE_URL = "https://mvdebook.blr1.digitaloceanspaces.com/media/";

  const showModal = () => {
    setFormData(emptyForm);

    setCoverFileList([]);
    setBannerFileList([]);

    setPreviewCover(null);
    setPreviewBanner(null);
    setModalKey((prev) => prev + 1);
    setIsModalVisible(true);
  };
  const hideModal = () => setIsModalVisible(false);

  const emptyForm = {
    id: null,
    title: "",
    code: "",
    author: "",
    language_id: "",
    category_id: "",
    state_id: "",
    status: "",
    mode: "",
    lastScriptNumber: "",
    description: "",
    cover_image: null,
    banner_image: null,
    is_featured: false,
    is_protected: false,
  };

  const fetchBooks = async () => {
    try {
      const [
        languagesRes,
        categoriesRes,
        stateRes,
        publishedRes,
        unpublishedRes,
      ] = await Promise.all([
        apiGet(apiRoutes.getLanguages),
        apiGet(apiRoutes.getCategories),
        apiGet(apiRoutes.getStates),
        apiGet(apiRoutes.getEbooks),
        apiGet(apiRoutes.getUnpublishedEbooks),
      ]);

      const languages = Array.isArray(languagesRes)
        ? languagesRes.map((lang) => ({
            label: lang.name,
            value: lang.id,
          }))
        : [];

      const states = Array.isArray(stateRes)
        ? stateRes.map((lang) => ({
            label: lang.name,
            value: lang.id,
          }))
        : [];

      const categories = Array.isArray(categoriesRes)
        ? categoriesRes.map((cat) => ({
            label: cat.name,
            value: cat.id,
          }))
        : [];

      setDropdownOptions({ languages, states, categories });

      if (Array.isArray(publishedRes.responsedata)) {
        setEbooks(publishedRes.responsedata);
      }

      if (Array.isArray(unpublishedRes.responsedata)) {
        setUnpublishedEbooks(unpublishedRes.responsedata);
      }
    } catch (err) {
      console.error("Error fetching dropdowns:", err.message);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const handleCloneBook = async (record) => {
    try {
      message.loading({ content: "Cloning book...", key: "clone" });

      const res = await apiPost(apiRoutes.cloneBook(record.id));

      message.success({
        content: "Book cloned successfully",
        key: "clone",
        duration: 2,
      });

      // Refetch covers
      await fetchBooks();
    } catch (error) {
      console.error("Clone failed:", error);

      message.error({
        content: error.response?.data?.message || "Failed to clone book",
        key: "clone",
        duration: 3,
      });
    }
  };

  const languageFilters = dropdownOptions.languages.map((lang) => ({
    text: lang.label,
    value: lang.label,
  }));

  const stateFilters = dropdownOptions.states.map((state) => ({
    text: state.label,
    value: state.label,
  }));

  const categoryFilters = dropdownOptions.categories.map((cat) => ({
    text: cat.label,
    value: cat.label,
  }));

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (text, record) => (
        <span
          onClick={() => handleEditModal(record)}
          style={{ color: "black", cursor: "pointer" }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      sorter: (a, b) => a.author.localeCompare(b.author),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: categoryFilters,
      render: (_, record) => record.category?.name || "—",
      onFilter: (value, record) => record.category.name === value,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      filters: languageFilters,
      render: (_, record) => record.language?.name || "—",
      onFilter: (value, record) => record.language.name === value,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      filters: stateFilters,
      render: (_, record) => record.state?.name || "—",
      onFilter: (value, record) => record.state.name === value,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        const bookstatus = record.is_active; // Access nested status with optional chaining
        return bookstatus === false ? "Draft" : "Published";
      },
      filters: [
        { text: "Published", value: "Published" },
        { text: "Draft", value: "Draft" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      render: (_, record) => {
        const mode = record.mode; // Access nested status with optional chaining
        return mode === "0" ? "Private" : "Public";
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu
              onClick={({ key }) => handleMenuClick(record, key)}
              items={[
                { label: "Edit Book", key: "edit", icon: <EditOutlined /> },
                {
                  label: "Edit Cover",
                  key: "cover",
                  icon: <FileSyncOutlined />,
                },
                { label: "View Book", key: "view", icon: <EyeOutlined /> },
                { label: "Clone Book", key: "clone", icon: <CopyOutlined /> },
                {
                  label: <span style={{ color: "red" }}>Delete Book</span>,
                  key: "delete",
                  icon: <DeleteOutlined style={{ color: "red" }} />,
                },
              ]}
            />
          }
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleDelete = async (record) => {
    console.log("Deleting book:", record);

    // TODO: Call your delete API here
    try {
      const response = await apiDelete(apiRoutes.deletebook(record.id));
      setEbooks((prev) => [...prev, response.data]);
      hideModal();
      fetchBooks();
      console.log("Book deleted successfully");
    } catch (error) {
      console.error(
        "Failed to delete book:",
        error.response?.data || error.message,
      );
    }
  };

  const handleMenuClick = (record, action) => {
    console.log(`Action: ${action} on`, record);
    if (action === "view") {
      navigate(`/bookdetails?id=${record.id}`);
    } else if (action === "edit") {
      navigate(
        `/managebook?id=${record.id}&book=${encodeURIComponent(record.title)}`,
      );
    } else if (action === "cover") {
      handleEditModal(record);
      console.log("Cover Edit:", record);
    } else if (action === "clone") {
      // Clone logic here
      handleCloneBook(record);
    } else if (action === "delete") {
      setSelectedRecord(record); // Save selected book
      setDeleteModalVisible(true); // Show delete modal
    }
  };

  const handleSubmit = async () => {
    try {
      const selectedLanguage = dropdownOptions.languages.find(
        (lang) => lang.value === formData.language_id,
      );

      const selectedState = dropdownOptions.states.find(
        (state) => state.value === formData.state_id,
      );

      const selectedCategory = dropdownOptions.categories.find(
        (cat) => cat.value === formData.category_id,
      );

      if (!selectedLanguage || !selectedCategory || !selectedState) {
        console.error("Invalid language or category or state selection");
        return;
      }

      const { language, category, ...rest } = formData;

      // ---------------------------------------
      // ⭐ PREPARE FormData object
      // ---------------------------------------
      const fd = new FormData();

      Object.keys(rest).forEach((key) => {
        if (key !== "cover_image" && key !== "banner_image") {
          fd.append(key, rest[key]);
        }
      });

      // Required backend fields
      fd.append("language_id", selectedLanguage.value);
      fd.append("category_id", selectedCategory.value);
      fd.append("state_id", selectedState.value);
      fd.append("is_active", formData.is_active);
      fd.append("created_by_id", 1);
      fd.append("last_updated_by_id", 1);
      fd.append("is_protected", formData.is_protected);
      fd.append("is_featured", formData.is_featured);

      // ---------------------------------------
      // ⭐ Append image files if present
      // ---------------------------------------
      if (formData.cover_image instanceof File) {
        fd.append("cover_image", formData.cover_image);
      }

      if (formData.banner_image instanceof File) {
        fd.append("banner_image", formData.banner_image);
      }

      // ---------------------------------------------------
      // ⭐ IF EDIT MODE (update)
      // ---------------------------------------------------
      if (formData.id) {
        console.log("Updating book with FormData:", formData, formData.id);

        const response = await apiPostFormData(
          apiRoutes.updateEBookCover(formData.id),
          fd,
        );

        fetchBooks();
        console.log("Book updated:", response.data);

        setEbooks((prev) =>
          prev.map((b) =>
            b.id === formData.id ? { ...b, ...response.data } : b,
          ),
        );

        hideModal();
        return;
      }

      // ---------------------------------------------------
      // ⭐ CREATE NEW BOOK
      // ---------------------------------------------------
      console.log("Creating book with FormData...");

      const response = await apiPostFormData(apiRoutes.createBook, fd);

      console.log("Book created:", response.data);

      setEbooks((prev) => [...prev, response.data]);
      fetchBooks();
      hideModal();
    } catch (error) {
      console.error(
        "Failed to save book:",
        error.response?.data || error.message,
      );
    }
  };

  const handleEditModal = (record) => {
    setFormData({
      id: record.id,
      title: record.title,
      code: record.code,
      author: record.author,
      language_id: record.language?.id,
      category_id: record.category?.id,
      description: record.description,
      // Convert boolean → string for dropdown
      is_active: record.is_active ? "true" : "false",
      // ⭐ FIXED HERE ⭐
      is_featured: record.is_featured === true,
      is_protected: record.is_protected === true,

      // Convert backend numeric mode → label used in dropdown
      mode: record.mode === "1" ? "Public" : "Private",
      lastScriptNumber: record.lastScriptNumber || 0,
      cover_image: record.cover_image,
      banner_image: record.banner_image,
    });

    setPreviewCover(
      record.cover_image ? IMAGE_BASE_URL + record.cover_image : null,
    );
    setPreviewBanner(
      record.banner_image ? IMAGE_BASE_URL + record.banner_image : null,
    );

    setModalKey((prev) => prev + 1);
    setIsModalVisible(true);
  };

  const handleCoverChange = (file) => {
    if (file) {
      setPreviewCover(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, cover_image: file }));
    } else {
      setPreviewCover(null);
      setFormData((prev) => ({ ...prev, cover_image: null }));
    }
  };

  const handleBannerChange = (file) => {
    if (file) {
      setPreviewBanner(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, banner_image: file }));
    } else {
      setPreviewBanner(null);
      setFormData((prev) => ({ ...prev, banner_image: null }));
    }
  };

  const filteredData = useMemo(() => {
    const list = activeTab === "published" ? ebooks : unpublishedEbooks;
    return list.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchText.toLowerCase()),
      ),
    );
  }, [searchText, ebooks, unpublishedEbooks, activeTab]);

  return (
    <div>
      <h1 className="text-xl font-semibold mb-4">Books List</h1>
      <div className="border border-grey rounded-md overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-[10px]">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            className="custom-tabs pl-4"
            items={[
              {
                key: "published",
                label: (
                  <span className="flex items-center gap-2">
                    Published
                    <Badge
                      count={ebooks.length}
                      style={{ backgroundColor: "#f1c40f", color: "black" }}
                    />
                  </span>
                ),
              },
              {
                key: "unpublished",
                label: (
                  <span className="flex items-center gap-2">
                    Unpublished
                    <Badge
                      count={unpublishedEbooks.length}
                      style={{ backgroundColor: "#f1c40f", color: "black" }}
                    />
                  </span>
                ),
              },
            ]}
          />
          <div className="flex items-center gap-3 mr-[10px]">
            <Space className="flex flex-wrap ml-[10px]" wrap>
              <Input
                placeholder="Search by title..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={handleSearch}
                className="w-[20vw] h-10"
              />
            </Space>

            <GlowButton
              className="mr-[10px]"
              style={{ backgroundColor: "black", borderColor: "black" }}
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
            >
              + Add New Book
            </GlowButton>
            <AddBookModal
              key={modalKey}
              visible={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              onSubmit={handleSubmit}
              formData={formData}
              setFormData={setFormData}
              dropdownOptions={dropdownOptions}
              previewCover={previewCover}
              previewBanner={previewBanner}
              onChangeCover={handleCoverChange}
              onChangeBanner={handleBannerChange}
            />
          </div>
        </div>
        <div className="border border-grey rounded-md overflow-hidden">
          <MyTable columns={columns} dataSource={filteredData} />
        </div>
      </div>
      <DeleteBookConfirmationModal
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onConfirm={() => {
          handleDelete(selectedRecord);
          setDeleteModalVisible(false);
        }}
      />
    </div>
  );
};

export default Ebook;
